import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { motion } from 'framer-motion';
import { HiChatAlt2, HiBadgeCheck, HiStar, HiSparkles } from 'react-icons/hi';

interface CommentItem {
  id: string;
  content: string;
  created_at: string;
  is_pinned?: boolean;
  is_approved?: boolean;
  user?: {
    display_name?: string | null;
    profile_image_url?: string | null;
  } | null;
  game?: {
    id: string;
    title: string;
    slug: string;
  } | null;
}

export default function FeaturedCommentsSection() {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedComments = async () => {
      try {
        const { data, error } = await supabase
          .from('comments')
          .select(`
            id,
            content,
            created_at,
            is_pinned,
            is_approved,
            user:user_id (
              display_name,
              profile_image_url
            ),
            game:game_id (
              id,
              title,
              slug
            )
          `)
          .eq('is_approved', true)
          .order('is_pinned', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(4);

        if (error) throw error;
        setComments((data as unknown as CommentItem[]) || []);
      } catch (err) {
        console.error('Error fetching featured comments for home:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedComments();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-black via-zinc-950 to-black relative z-10 border-t border-white/5">
      <div className="container mx-auto px-4 md:px-8">
        {/* هدر بخش */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-12">
          <div>
            <div className="flex items-center gap-2 text-primary font-bold text-xs md:text-sm mb-2">
              <HiSparkles className="w-4 h-4" />
              <span>دیدگاه‌ها و تجربیات گیمرها</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-white tracking-tight">
              نظرات برگزیده بازیکنان
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm text-zinc-400">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>نظرات تایید شده توسط تیم Active Legend</span>
          </div>
        </div>

        {/* لیست دیدگاه‌ها */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="h-48 rounded-3xl bg-white/5 animate-pulse border border-white/10" />
            ))}
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 rounded-3xl bg-white/5 border border-white/10">
            <HiChatAlt2 className="w-10 h-10 text-primary/40 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">هنوز دیدگاهی به عنوان نظر برگزیده تایید نشده است.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {comments.map((comment) => (
              <motion.div
                key={comment.id}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
                className="group relative bg-zinc-900/60 backdrop-blur-md rounded-3xl p-6 border border-white/10 hover:border-primary/40 shadow-xl flex flex-col justify-between transition-all"
              >
                {/* آیکون پس‌زمینه کوتیشن */}
                <div className="absolute top-4 left-4 text-white/5 group-hover:text-primary/10 transition-colors pointer-events-none">
                  <HiChatAlt2 className="w-12 h-12" />
                </div>

                <div>
                  {/* اطلاعات کاربر و نشان تایید */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden border border-white/20 bg-zinc-800 flex items-center justify-center shrink-0">
                      {comment.user?.profile_image_url ? (
                        <img
                          src={comment.user.profile_image_url}
                          alt={comment.user.display_name || 'کاربر'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = '/AE logo.svg';
                          }}
                        />
                      ) : (
                        <span className="text-primary font-bold text-base">
                          {(comment.user?.display_name || 'U').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-white font-bold text-sm truncate">
                          {comment.user?.display_name || 'کاربر اکتیو لجند'}
                        </span>
                        <HiBadgeCheck
                          className="w-4 h-4 text-emerald-400 shrink-0"
                          title="کاربر و نظر تایید شده"
                        />
                      </div>
                      <span className="text-zinc-500 text-xs block">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* متن دیدگاه */}
                  <p className="text-zinc-300 text-sm leading-relaxed mb-4 line-clamp-4 relative z-10 font-normal">
                    «{comment.content}»
                  </p>
                </div>

                {/* فوتر کارت: تگ بازی یا وضعیت */}
                <div className="pt-3 border-t border-white/5 flex items-center justify-between text-xs text-zinc-400">
                  {comment.game ? (
                    <Link
                      to={`/games/${comment.game.slug}`}
                      className="text-primary/80 hover:text-primary font-medium truncate max-w-[160px] transition-colors"
                    >
                      🎮 {comment.game.title}
                    </Link>
                  ) : (
                    <span className="text-zinc-500 flex items-center gap-1">
                      <HiStar className="w-3.5 h-3.5 text-primary/70" />
                      دیدگاه عمومی
                    </span>
                  )}
                  {comment.is_pinned && (
                    <span className="text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full text-[10px] font-bold">
                      برگزیده
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
